import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../service/auth.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  resetForm: FormGroup;
  loading = false;
  token: string | null = null;
  passwordVisible = false;
  confirmPasswordVisible = false;
  passwordStrength = {
    score: 0,
    feedback: '',
    color: ''
  };

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(5)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // Watch password changes for strength calculation
    this.resetForm.get('password')?.valueChanges.subscribe(value => {
      this.calculatePasswordStrength(value);
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || null;
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
      return { mismatch: true };
    }
    return null;
  }

  async onSubmit() {
    if (this.resetForm.valid && this.token) {
      this.loading = true;
      const { password } = this.resetForm.value;

      try {
        await this.authService.resetPassword(this.token, password);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Password reset successfully'
        });
        this.router.navigate(['/login']);
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reset password. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    }
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleConfirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  calculatePasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = { score: 0, feedback: '', color: '' };
      return;
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Determine strength
    if (score < 3) {
      this.passwordStrength = {
        score,
        feedback: 'Weak',
        color: '#ef4444'
      };
    } else if (score < 5) {
      this.passwordStrength = {
        score,
        feedback: 'Medium',
        color: '#f59e0b'
      };
    } else {
      this.passwordStrength = {
        score,
        feedback: 'Strong',
        color: '#10b981'
      };
    }
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}